import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { makeStyles, TextField } from '@material-ui/core';
import { RegisterRfqInput } from '@generated/graphql';
import { RootState } from '@modules/index';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestInputCommonProps } from './types';
import useRequest from '@hooks/useRequest';

const DesignPrototypingControl: React.FC<RequestInputCommonProps> = ({
  error
}) => {
  const requestPrototypingDefaultValue = useSelector<RootState, boolean | null>(
    (state) => state.request.requestPrototyping
  );
  const prototypeQuantity = useSelector<RootState, number | null>(
    (state) => state.request?.prototypeQuantity ?? null
  );

  const [prototypeCount, setPrototypeCount] = useState(prototypeQuantity);
  const [requestPrototyping, setRequestPrototyping] = useState<boolean | null>(
    requestPrototypingDefaultValue !== null
      ? requestPrototypingDefaultValue
      : null
  );
  const { onUpdateRfqInfo } = useRequest();
  const { controlContainer, quantityInput } = useStyles();
  const { control, errors, setValue } = useFormContext<
    RegisterRfqInput & {
      requestPrototyping: boolean | null;
      prototypeQuantity: number | null;
    }
  >();
  const quantityError = errors?.prototypeQuantity?.message;

  useEffect(() => {
    if (requestPrototyping === null) return;

    setValue(
      'prototypeQuantity',
      requestPrototyping ? prototypeQuantity : null
    );
    onUpdateRfqInfo({
      prototypeQuantity: requestPrototyping ? prototypeQuantity : null
    });

    //eslint-disable-next-line
  }, [requestPrototyping, onUpdateRfqInfo]);

  return (
    <>
      <FormInputLayout label="시제품제작 여부 선택" required={true}>
        <Controller
          contorl={control}
          rules={{
            required: {
              message: '시제품 제작 여부를 선택해주세요',
              value: true
            }
          }}
          name={'requestPrototyping'}
          defaultValue={
            requestPrototypingDefaultValue !== null
              ? requestPrototypingDefaultValue
              : null
          }
          render={(renderProps) => (
            <div className={controlContainer}>
              <div
                data-cy="rfq-design-requestPrototyping-true"
                className={clsx([
                  'switch',
                  {
                    active: requestPrototyping !== null && !!requestPrototyping
                  }
                ])}
                onClick={(e) => {
                  setRequestPrototyping(true);
                  renderProps.onChange(true);
                }}
              >
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  예, 제작합니다.
                </CustomizedTypography>
              </div>
              <div
                data-cy="rfq-design-requestPrototyping-false"
                className={clsx([
                  'switch',
                  {
                    active: requestPrototyping !== null && !requestPrototyping
                  }
                ])}
                onClick={(e) => {
                  setRequestPrototyping(false);
                  renderProps.onChange(false);
                }}
              >
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  아니요, 제작하지 않습니다.
                </CustomizedTypography>
              </div>
            </div>
          )}
        />

        {error && (
          <CustomizedTypography variant="caption" color="error">
            {error}
          </CustomizedTypography>
        )}
      </FormInputLayout>

      {requestPrototyping && (
        <FormInputLayout label="시제품제작 수량" required={true}>
          <Controller
            name="prototypeQuantity"
            control={control}
            rules={{
              required: {
                value: true,
                message: '시제품제작 수량을 입력해주세요'
              },
              validate: {
                isNumber: (value) =>
                  Number.isNaN(Number(value)) ? '숫자만 입력해주세요' : true,
                isOver20length: (value) =>
                  value.toString().length < 20
                    ? true
                    : '20자리 미만이어야합니다.'
              }
            }}
            render={(renderProps) => (
              <TextField
                data-cy="rfq-design-prototypeQuantity"
                className={quantityInput}
                variant="outlined"
                fullWidth
                placeholder="0"
                type="number"
                value={prototypeCount}
                helperText={quantityError}
                error={!!quantityError}
                InputProps={{
                  endAdornment: (
                    <CustomizedTypography color="textSecondary" variant="body1">
                      개
                    </CustomizedTypography>
                  )
                }}
                onInput={(e: any) => {
                  const prototypeQuantity = +e.target.value.replace(
                    /[^0-9.]/g,
                    ''
                  );

                  const isZero = prototypeQuantity === 0;

                  setPrototypeCount(isZero ? null : prototypeQuantity);
                  renderProps.onChange(prototypeQuantity);
                  onUpdateRfqInfo({ prototypeQuantity });
                }}
              />
            )}
          />
        </FormInputLayout>
      )}
    </>
  );
};

export default DesignPrototypingControl;

const useStyles = makeStyles((theme) => ({
  controlContainer: {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '8px'
    },
    '& .switch': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 18px',
      borderRadius: '12px',
      border: `1px solid ${colorSet.gray400}`,
      gap: '12px',
      '& img': {
        marginBottom: '12px'
      },
      '&:hover': {
        cursor: 'pointer'
      },
      '&.active': {
        backgroundColor: colorSet.gray100
      },
      '&:hover, &.active': {
        border: `2px solid ${colorSet.gray900}`,
        padding: '19px 17px'
      }
    }
  },
  quantityInput: {
    '& input': {
      padding: 12,
      textAlign: 'right'
    },
    '& .MuiInputBase-root': {
      alignItems: 'center',
      borderRadius: 8
    },
    '& ::placeholder': {
      color: colorSet.gray500
    }
  }
}));
